import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE_NUMBER,
  INITIAL_ITEMS_PER_PAGE,
} from '@components/listing-realisations/config';
import { ListingRealisations } from '@components/listing-realisations/models';
import { PopinRealisationsClientsComponent } from '@components/popin-realisations-clients';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ButtonMiddleOfficeComponent } from '@shared/components/button-middle-office';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { Project } from '@shared/models';
import { DialogService } from '@shared/services/dialog';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { ShowcaseQueryOptions } from '@shared/services/elastic-search/queries';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

const contextMap = { mur: 'wall', 'listing-magasin': 'store' };

@Component({
  selector: 'app-listing-realisations',
  templateUrl: './listing-realisations.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, TranslatePipe, ImageComponent, SvgComponent, ButtonMiddleOfficeComponent],
})
export class ListingRealisationsComponent extends AbstractDynamicComponent<ListingRealisations> {
  protected readonly projects = signal<Project[]>([]);
  protected readonly maxItems = signal(0);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly urlParamsService = inject(UrlParamsService);
  protected readonly layoutFilter = this.urlParamsService.param('filter_layout');
  protected readonly frontFilter = this.urlParamsService.param('filter_front');
  protected readonly currentPage = signal(Number(this.urlParamsService.param('page')()) || DEFAULT_PAGE_NUMBER);
  private readonly dialogService = inject(DialogService);
  private readonly logs = inject(LogsService);
  private readonly previousFilters = signal(`l${untracked(this.layoutFilter)}f${untracked(this.frontFilter)}`);

  constructor() {
    super();

    effect(() => {
      this.urlParamsService.mergeParams({ page: this.currentPage() });
    });

    effect(() => {
      const currentFilters = `l${this.layoutFilter()}f${this.frontFilter()}`;
      const previous = this.previousFilters();

      if (previous === currentFilters) return;

      this.currentPage.set(DEFAULT_PAGE_NUMBER);
      this.previousFilters.set(currentFilters);
    });

    effect(() => {
      if (!this.data) return;

      const currentPage = this.currentPage();
      const isInit = untracked(this.projects).length === 0;

      const showcaseQueryOptions: ShowcaseQueryOptions = {
        context: contextMap[this.data.context] as 'store' | 'wall',
        storeUuid: this.data.storeUuid,
        layout: this.layoutFilter() ?? undefined,
        front: this.frontFilter() ?? undefined,
        from: isInit ? 0 : INITIAL_ITEMS_PER_PAGE + (currentPage - 2) * DEFAULT_ITEMS_PER_PAGE,
        size: isInit
          ? currentPage === 1
            ? INITIAL_ITEMS_PER_PAGE
            : INITIAL_ITEMS_PER_PAGE + DEFAULT_ITEMS_PER_PAGE * (currentPage - 1)
          : DEFAULT_ITEMS_PER_PAGE,
      };

      this.elasticSearchService
        .getShowcases(showcaseQueryOptions)
        .pipe(
          take(1),
          tap((res) => {
            if (currentPage === 1) this.projects.set(res.showcases);
            else this.projects.update((prev) => [...prev, ...res.showcases]);
            this.maxItems.set(res.total);
          })
        )
        .subscribe({ error: (err) => this.logs.error('ListingRealisationsComponent', 'getShowcases failed', err) });
    });
  }

  loadNextPage() {
    this.currentPage.update((p) => p + 1);
  }

  openRealisationsClientsPopup(selectedProjectIndex: number) {
    const projects = this.projects();
    if (projects.length === 0) return;
    this.dialogService.openDialog(PopinRealisationsClientsComponent, 'BIG', {
      data: {
        selectedProjectIndex,
        projects,
      },
    });
  }
}
